import React, { useState, useMemo } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonChip,
  IonLabel,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import {
  cubeOutline,
  ticketOutline,
  layersOutline,
  gridOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import { Ticket } from '../types';
import { SPLIT_TYPE_OPTIONS } from '../utils/ticketConstants';

interface InventoryOverviewProps {
  tickets: Ticket[];
  loading: boolean;
}

const InventoryOverview: React.FC<InventoryOverviewProps> = ({ tickets, loading }) => {
  const [viewMode, setViewMode] = useState<'category' | 'block'>('category');

  // Helper function to calculate groups based on quantity and split type
  const calculateGroups = (quantity: number, splitOption: any) => {
    const result: { [groupSize: string]: number } = {};
    let remaining = quantity;

    if (splitOption.maxGroup === -1) {
      // All together
      const groupKey = remaining === 1 ? 'singles' : 
                      remaining === 2 ? 'pairs' : 
                      remaining === 3 ? 'trios' : 
                      remaining === 4 ? 'quads' : 
                      `${remaining} together`;
      result[groupKey] = 1;
    } else if (splitOption.maxGroup === 1) {
      // Singles only
      result['singles'] = remaining;
    } else {
      // Calculate groups based on max group size
      while (remaining > 0) {
        if (remaining >= splitOption.maxGroup) {
          const groupKey = splitOption.maxGroup === 2 ? 'pairs' : 
                         splitOption.maxGroup === 3 ? 'trios' : 
                         splitOption.maxGroup === 4 ? 'quads' : 
                         `${splitOption.maxGroup} together`;
          result[groupKey] = (result[groupKey] || 0) + 1;
          remaining -= splitOption.maxGroup;
        } else {
          // Handle remaining tickets
          const groupKey = remaining === 1 ? 'singles' : 
                         remaining === 2 ? 'pairs' : 
                         remaining === 3 ? 'trios' : 
                         `${remaining} together`;
          result[groupKey] = (result[groupKey] || 0) + 1;
          remaining = 0;
        }
      }
    }
    return result;
  };

  // Calculate available and sold inventory by category/block and split type
  const { availableGroups, soldGroups } = useMemo(() => {
    const available: {
      [key: string]: {
        [groupSize: string]: number;
      };
    } = {};
    const sold: {
      [key: string]: {
        [groupSize: string]: number;
      };
    } = {};

    // Process all tickets
    tickets.forEach((ticket) => {
      if (!ticket.category || !ticket.quantity) return;

      // Calculate quantities
      const availableQty = ticket.quantity - (ticket.soldQuantity || 0);
      const soldQty = ticket.soldQuantity || 0;

      // Determine grouping key based on view mode
      let key: string;
      if (viewMode === 'category') {
        key = ticket.category;
      } else {
        // Block view - include block in the key
        key = ticket.block ? `${ticket.category} - ${ticket.block}` : ticket.category;
      }

      const splitType = ticket.split || 'all-together';
      const splitOption = SPLIT_TYPE_OPTIONS.find(opt => opt.id === splitType);
      
      if (!splitOption) return;

      // Process available tickets
      if (availableQty > 0) {
        if (!available[key]) {
          available[key] = {};
        }
        const groups = calculateGroups(availableQty, splitOption);
        Object.entries(groups).forEach(([groupType, count]) => {
          available[key][groupType] = (available[key][groupType] || 0) + count;
        });
      }

      // Process sold tickets
      if (soldQty > 0) {
        if (!sold[key]) {
          sold[key] = {};
        }
        const groups = calculateGroups(soldQty, splitOption);
        Object.entries(groups).forEach(([groupType, count]) => {
          sold[key][groupType] = (sold[key][groupType] || 0) + count;
        });
      }
    });

    return { availableGroups: available, soldGroups: sold };
  }, [tickets, viewMode]);

  const calculateTotal = (groups: { [key: string]: { [groupSize: string]: number } }) => {
    return Object.values(groups).reduce(
      (total: number, categoryGroups: { [groupSize: string]: number }) =>
        total +
        Object.entries(categoryGroups).reduce((catTotal, [groupType, count]) => {
          // Extract number from group type
          const match = groupType.match(/^(\d+) together$/);
          if (match) {
            return catTotal + (parseInt(match[1]) * (count as number));
          } else if (groupType === 'singles') {
            return catTotal + (count as number);
          } else if (groupType === 'pairs') {
            return catTotal + (2 * (count as number));
          } else if (groupType === 'trios') {
            return catTotal + (3 * (count as number));
          } else if (groupType === 'quads') {
            return catTotal + (4 * (count as number));
          }
          return catTotal;
        }, 0),
      0
    );
  };

  const totalAvailable = calculateTotal(availableGroups);
  const totalSold = calculateTotal(soldGroups);

  if (loading || (Object.keys(availableGroups).length === 0 && Object.keys(soldGroups).length === 0)) {
    return null;
  }

  // Get all unique category keys
  const allKeys = Array.from(new Set([...Object.keys(availableGroups), ...Object.keys(soldGroups)]));

  return (
    <div className="px-4 mb-4">
      <IonCard className="shadow-lg">
        <IonCardHeader className="bg-primary-50 dark:bg-primary-900/30">
          <div className="flex items-center justify-between">
            <IonCardTitle className="text-lg flex items-center">
              <IonIcon icon={cubeOutline} className="mr-2" />
              Inventory Overview
            </IonCardTitle>
            <IonSegment
              value={viewMode}
              onIonChange={(e) => setViewMode(e.detail.value as 'category' | 'block')}
              className="w-auto"
              style={{ maxWidth: '280px' }}
            >
              <IonSegmentButton value="category">
                <IonLabel className="text-xs">
                  <IonIcon icon={layersOutline} className="mr-1" />
                  Category
                </IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="block">
                <IonLabel className="text-xs">
                  <IonIcon icon={gridOutline} className="mr-1" />
                  Block
                </IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </div>
        </IonCardHeader>
        <IonCardContent>
          <div className="space-y-4 mt-4">
            {allKeys.sort().map((groupName) => {
              const hasAvailable = availableGroups[groupName] && Object.keys(availableGroups[groupName]).length > 0;
              const hasSold = soldGroups[groupName] && Object.keys(soldGroups[groupName]).length > 0;
              
              return (
                <div key={groupName} className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-medium text-base mb-2 text-gray-900 dark:text-gray-100">
                    {groupName}
                  </h4>
                  
                  {/* Available inventory */}
                  {hasAvailable && (
                    <div className="mb-3">
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">
                        Available to sell:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(availableGroups[groupName])
                          .sort(([a], [b]) => {
                            const sizeA = a === 'singles' ? 1 : a === 'pairs' ? 2 : a === 'trios' ? 3 : a === 'quads' ? 4 : 5;
                            const sizeB = b === 'singles' ? 1 : b === 'pairs' ? 2 : b === 'trios' ? 3 : b === 'quads' ? 4 : 5;
                            return sizeA - sizeB;
                          })
                          .map(([groupType, count]) => (
                            <IonChip key={`${groupName}-available-${groupType}`} color="success">
                              <IonIcon icon={ticketOutline} />
                              <IonLabel className="font-medium">
                                {count} × {groupType}
                              </IonLabel>
                            </IonChip>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Sold inventory */}
                  {hasSold && (
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                        Already sold:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(soldGroups[groupName])
                          .sort(([a], [b]) => {
                            const sizeA = a === 'singles' ? 1 : a === 'pairs' ? 2 : a === 'trios' ? 3 : a === 'quads' ? 4 : 5;
                            const sizeB = b === 'singles' ? 1 : b === 'pairs' ? 2 : b === 'trios' ? 3 : b === 'quads' ? 4 : 5;
                            return sizeA - sizeB;
                          })
                          .map(([groupType, count]) => (
                            <IonChip key={`${groupName}-sold-${groupType}`} color="primary">
                              <IonIcon icon={checkmarkCircleOutline} />
                              <IonLabel className="font-medium">
                                {count} × {groupType} ✓
                              </IonLabel>
                            </IonChip>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Summary:</strong>
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                <strong>Available to sell:</strong> {totalAvailable} tickets
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                <strong>Already sold:</strong> {totalSold} tickets
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Total inventory:</strong> {totalAvailable + totalSold} tickets across {allKeys.length}{' '}
                {viewMode === 'category' ? 'categories' : 'groups'}
              </p>
            </div>
          </div>
        </IonCardContent>
      </IonCard>
    </div>
  );
};

export default InventoryOverview;